'use client';

import type { User } from '@supabase/supabase-js';
import { create } from 'zustand';

/**
 * 순수 인증 상태 (Pure Auth State)
 *
 * Feature 레벨 비즈니스 로직에 의존하지 않는 최소한의 인증 상태.
 * Supabase 구독 관리는 AuthStoreInitializer에서 처리.
 */
interface AuthState {
  user: User | null;
  isLoading: boolean;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  reset: () => void;
}

interface AuthGetters {
  /** 게스트(비로그인) 상태 여부 */
  getIsGuest: () => boolean;
}

type AuthStore = AuthState & AuthActions & AuthGetters;

const initialState: AuthState = {
  user: null,
  isLoading: true,
};

/**
 * 순수 인증 상태 스토어
 *
 * @example
 * ```tsx
 * // User 설정 (isLoading 자동 설정)
 * const { setUser } = useAuth.getState();
 * setUser(user);  // isLoading 자동 업데이트
 *
 * // 컴포넌트에서 사용
 * const user = useAuth(s => s.user);
 * const isGuest = useAuth(s => s.getIsGuest());
 *
 * // 전체 상태 접근
 * const { user, isLoading } = useAuth();
 * ```
 */
export const useAuth = create<AuthStore>()((set, get) => ({
  ...initialState,

  // Getters
  getIsGuest: () => get().user === null,

  // Actions
  setUser: (user: User | null) => {
    set({
      user,
      isLoading: false,
    });
  },
  reset: () => set({ ...initialState }),
}));

// Type exports
export type { AuthState, AuthActions, AuthGetters, AuthStore };
